import {
  Avatar,
  Flex,
  Link,
  SkeletonText,
  SkeletonShape,
  FormattedRelativeTime,
  Text,
} from '@customerio/pluma-components/react';
import type { ServicesApiShippedResource } from '@/services/api/services-api';
import { isEmptyString } from '@/utils/string';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useResourceInfo } from '../hooks';
import { resourceHref } from './recents-row';
import * as styles from './recently-launched.css';

const TYPE_FALLBACK_LABELS: Record<string, string> = {
  campaign: 'a campaign',
  newsletter: 'a broadcast',
  transactional: 'a transactional message',
  in_app_broadcast: 'an in-app message',
};

type ShippedResourceWithDisplayName = ServicesApiShippedResource & { displayName?: string };

interface ActivityItemProps {
  resource: ShippedResourceWithDisplayName;
  workspaceId: number;
}

function ActivityItem({ resource, workspaceId }: ActivityItemProps) {
  const { track } = useAnalytics();
  const { name, isNotFound } = useResourceInfo(workspaceId, resource.type, resource.id);
  const { user } = useAuth();
  const isCurrentUser = resource.user_id > 0 && resource.user_id === user.id;
  const displayName =
    resource.displayName ?? name ?? (isNotFound ? TYPE_FALLBACK_LABELS[resource.type] ?? resource.type : undefined);
  const avatarName = resource.user_id > 0 && !isEmptyString(resource.user_name) ? resource.user_name : 'System';

  return (
    <div className={styles.item}>
      <Avatar name={avatarName} autoColor />
      <Flex direction="column" gap="025" minW="0" flexGrow="1" flexShrink="1">
        <Text as="div" text="product-ps" color="subtle">
          {isCurrentUser ? 'You' : avatarName} {resource.action}{' '}
          <Link
            href={resourceHref(workspaceId, resource)}
            variant="primary"
            onClick={() =>
              track('home', 'recentActivityClicked', { 'Resource Type': resource.type, Action: resource.action })
            }
          >
            {displayName ?? <SkeletonShape display="inline-block" style={{ width: 80, height: 14 }} />}
          </Link>
        </Text>
        <Text text="product-pxs" color="subtle">
          <FormattedRelativeTime date={resource.updated_at * 1000} />
        </Text>
      </Flex>
    </div>
  );
}

interface RecentlyLaunchedProps {
  shipped: ShippedResourceWithDisplayName[] | undefined;
  isLoading: boolean;
  workspaceId: number;
}

export function RecentlyLaunched({ shipped, isLoading, workspaceId }: RecentlyLaunchedProps) {
  if (isLoading) {
    return (
      <Flex direction="column" gap="050">
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className={styles.item}>
            <SkeletonShape className={styles.skeletonCircle} />
            <Flex direction="column" gap="025" minW="0" flexGrow="1" flexShrink="1">
              <SkeletonText lines={2} />
            </Flex>
          </div>
        ))}
      </Flex>
    );
  }

  if (shipped == null || shipped.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" gap="050">
      {shipped.map((resource) => (
        <ActivityItem key={`${resource.type}-${resource.id}`} resource={resource} workspaceId={workspaceId} />
      ))}
    </Flex>
  );
}
